import type { CallType } from '@klaro7/shared-types';

/**
 * Device-permission pre-flight, run BEFORE any call signaling (initiate or
 * accept). Acquiring — then immediately stopping — a stream at click time
 * runs inside the user gesture, so the browser shows its permission prompt
 * NOW instead of mid-call, and a denial surfaces as a clear message instead
 * of a silent, dead call. Returns null when the devices are usable, or an
 * Italian user-facing error message otherwise.
 */
export async function ensureMediaPermissions(callType: CallType): Promise<string | null> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: callType === 'video' ? { facingMode: 'user' } : false,
    });
    // Permission granted — release the devices; the real call re-acquires them.
    stream.getTracks().forEach((track) => track.stop());
    return null;
  } catch (err) {
    const name = err instanceof DOMException ? err.name : '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      return callType === 'video'
        ? 'Permessi microfono e fotocamera negati. Consentili nelle impostazioni del browser (icona lucchetto vicino all\'indirizzo) e riprova.'
        : 'Permesso microfono negato. Consentilo nelle impostazioni del browser (icona lucchetto vicino all\'indirizzo) e riprova.';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return callType === 'video'
        ? 'Nessun microfono o fotocamera rilevati sul dispositivo.'
        : 'Nessun microfono rilevato sul dispositivo.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return 'Microfono occupato da un\'altra app. Chiudila e riprova.';
    }
    return 'Impossibile accedere al microfono. Controlla i permessi del dispositivo e riprova.';
  }
}
